import { ExInput, ExButton, ButtonType, ButtonFlavor } from '@boomi/exosphere';
import translations from '../../../translations';
import GenericModal from '../../generic/modal/GenericModal';
import ModalBody from '../../generic/modal/ModalBody';
import ModalFooter from '../../generic/modal/ModalFooter';
import { useEffect, useRef, useState, type KeyboardEventHandler } from 'react';
import { setLatest } from '../../../sources/flow';
import Loader from '../../loader/Loader';
import type Input from '@boomi/exosphere/dist/components/input/input.js';

type Props = {
    show: boolean;
    onCancel: () => void;
    onFlowCreated: (flowId: string) => void;
    onError: (error: Error) => void;
    ref: HTMLElement | null;
};

const NewFlowModal = ({ onCancel, onFlowCreated, show, onError, ref }: Props) => {
    const [flowName, setFlowName] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<Input>(null);
    const isValid = flowName.length > 0;

    const resetForm = () => {
        setHasSubmitted(false);
        setFlowName('');
    };

    const handleSave = async () => {
        setHasSubmitted(true);

        if (isValid) {
            setIsLoading(true);
            try {
                const response = await setLatest({
                    id: null,
                    developerName: flowName,
                    developerSummary: '',
                    startMapElementId: null,
                    allowJumping: false,
                    enableHistoricalNavigation: false,
                    stateExpirationLength: 0,
                    idleStateExpirationLength: 0,
                    stateLockedByUserModification: false,
                    authorization: {
                        serviceElementId: null,
                        globalAuthenticationType: 'PUBLIC',
                        streamBehaviourType: 'NONE',
                        showPagesAsReadOnly: false,
                        groups: [],
                        users: [],
                        locations: [],
                    },
                    displayRootFaults: false,
                });
                onFlowCreated(response.id.id);
                resetForm();
            } catch (e) {
                onError(e as Error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleKeyUp: KeyboardEventHandler<Input> = (event) => {
        if (event.key === 'Enter') {
            handleSave();
        }
    };

    const handleCancel = () => {
        onCancel();
        resetForm();
    };

    useEffect(() => {
        if (show) {
            requestAnimationFrame(() => {
                if (inputRef.current) {
                    inputRef.current.focus(undefined);
                }
            });
        }
    }, [show]);

    return (
        <GenericModal
            show={show}
            onHide={handleCancel}
            title={translations.FLOW_CREATE_NEW}
            className="new-flow-modal modal-dialog-small"
            container={ref}
        >
            <ModalBody className="auto-height">
                {isLoading && <Loader />}
                <ExInput
                    ref={inputRef}
                    data-testid="flow-name-input"
                    label="Name"
                    value={flowName}
                    onInput={(event) => setFlowName((event.target as HTMLInputElement).value)}
                    required={true}
                    invalid={hasSubmitted && !isValid}
                    error-msg="This field is required."
                    type="text"
                    onKeyUp={handleKeyUp}
                />
            </ModalBody>

            <ModalFooter className="buttons-right">
                <ExButton
                    type={ButtonType.SECONDARY}
                    flavor={ButtonFlavor.BRANDED}
                    onClick={handleCancel}
                    data-testid="create-flow-cancel-button"
                >
                    {translations.COMMON_cancel}
                </ExButton>
                <ExButton
                    type={ButtonType.PRIMARY}
                    flavor={ButtonFlavor.BRANDED}
                    onClick={handleSave}
                    data-testid="create-flow-button"
                >
                    {translations.COMMON_save}
                </ExButton>
            </ModalFooter>
        </GenericModal>
    );
};

export default NewFlowModal;
